// Where a payout's money went, ready to draw — whoever paid it.
//
// The server sends `destination` in one shape for every payout provider
// (Stripe bank and Instant-to-card payouts today, MassPay wallet loads next;
// server lib/payoutRowView.js), so nothing here knows a provider by name.
// A server from before that field sent only the bank snapshot
// (bankLast4 / bankBrand) — read as a bank, which is all it could have been.
import type { PayoutBatch } from "../types/payouts";
import { t } from "../i18n";

/** The icon to draw. "other" = a kind this build doesn't know yet (a newer server's). */
export type DestinationIconKind = "bank" | "card" | "wallet" | "other";

export interface DestinationView {
  kind: DestinationIconKind;
  /** The short line under the date: "•••8024", or the name when there is no number ("MassPay"). */
  text: string;
  /** The whole thing, for the tap-and-hold / hover title: "BANK OF AMERICA •••8024". */
  title: string;
}

// Stripe's bank names carry the legal suffix ("BANK OF AMERICA, N.A.") —
// dropped, as on the Balance tab: it says nothing and costs the line its room.
const tidyName = (name: string | null | undefined) => (name || "").replace(/,?\s+N\.?\s?A\.?$/i, "").trim() || null;

const KNOWN: readonly string[] = ["bank", "card", "wallet"];

export function payoutDestinationView(p: Pick<PayoutBatch, "destination" | "bankLast4" | "bankBrand">): DestinationView | null {
  const d =
    p.destination !== undefined
      ? p.destination
      : p.bankLast4 || p.bankBrand
        ? { kind: "bank", name: p.bankBrand ?? null, last4: p.bankLast4 ?? null }
        : null;
  if (!d) return null;
  const kind = (KNOWN.includes(d.kind) ? d.kind : "other") as DestinationIconKind;
  const name = tidyName(d.name);
  const last4 = d.last4 || null;
  if (!name && !last4) return null;
  const number = last4 ? `•••${last4}` : "";
  const label = name || (kind === "card" ? t("balancePage.debitCard") : kind === "bank" ? t("balancePage.bank") : "");
  return { kind, text: number || (name as string), title: [label, number].filter(Boolean).join(" ") };
}
